import * as React from 'react';
import { map, sortBy } from 'lodash';
import { Role } from 'auth';
import './organization-list.scss';
import OrganizationEdit from './organization-edit';
import OrganizationConnect from './organization-connect';
import OrganizationAddRoles from './organization-add-roles';
import { GqlOrganization } from '../../core/gql/fragments/organization';
import * as AllOrganizations from '../../core/gql/queries/all-organizations';
import { useLazyQuery } from '@apollo/client';
import { Button, Panel, PanelBody, PanelFooter, PanelRow, PanelCell, Input } from '@teamsnap/teamsnap-ui';
import { useLocation } from 'react-router-dom';
import { DateFormat, DateService } from 'frontend-toolkit';
import { LabelButton } from '@teamsnap/snap-ui';
import { ADMIN_URL } from 'core/constants';

const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const OrganizationList = () => {
  const [selectedOrganization, setSelectedOrganization] = React.useState<GqlOrganization>(null);
  const [showOrgConnectModal, setShowOrgConnectModal] = React.useState(false);
  const [showOrgEditModal, setShowOrgEditModal] = React.useState(false);
  const [showAddRolesModal, setShowAddRolesModal] = React.useState(false);
  const [searchTerm, setsearchTerm] = React.useState('');
  const [showTestOrgs, setShowTestOrgs] = React.useState(false);
  const [totalTestOrgs, setTotalTestOrgs] = React.useState(0);
  const [getAllOrganizations, { data, loading }] = useLazyQuery(AllOrganizations.QUERY, { errorPolicy: 'all' });

  const query = useQuery();

  const populateSearchTermWithOrgId = React.useCallback(
    (orgId: string, isTest: boolean) => {
      setsearchTerm(orgId);
      setShowTestOrgs(isTest);
      getAllOrganizations({ variables: { searchTerm: orgId } });
    },
    [getAllOrganizations]
  );

  const populateSearchTerm = React.useCallback(
    (searchTerm: string) => {
      setsearchTerm(searchTerm);
      getAllOrganizations({ variables: { searchTerm: searchTerm } });
    },
    [getAllOrganizations]
  );

  React.useEffect(() => {
    if (query.get('search_term')) {
      populateSearchTerm(query.get('search_term'));
    }

    if (query.get('orgId')) {
      const isTest = query.get('isTest') === 'true' ? true : false;
      populateSearchTermWithOrgId(query.get('orgId'), isTest);
    }
  }, [populateSearchTerm, populateSearchTermWithOrgId, query]);

  React.useEffect(() => {
    if (data?.allOrganizations && data.allOrganizations.length > 0) {
      setTotalTestOrgs(data.allOrganizations.filter((org) => org.isTest).length);
    }
  }, [data]);

  const showOrganization = (organization: GqlOrganization) => {
    setSelectedOrganization(organization || null);
    setShowOrgEditModal(true);
  };

  const showRolesModal = (organization: GqlOrganization) => {
    setSelectedOrganization(organization || null);
    setShowAddRolesModal(true);
  };

  const renderLoading = () => (
    <PanelRow mods="u-textCenter">
      <div className="Loader-indicator">
        <span className="SpinAnimation" />
        <div className="Loader-indicatorText">Loading organizations...</div>
      </div>
    </PanelRow>
  );

  const renderNoOrgs = () => (
    <PanelRow mods="u-textCenter">
      <h4>
        No organizations currently exist.&nbsp;&nbsp;
        <Button color="primary" icon="plus" iconPosition="left" onClick={() => showOrganization(null)}>
          Create Organization
        </Button>
      </h4>
    </PanelRow>
  );

  const renderOrganization = (organization: GqlOrganization) => {
    let ownerEmail = null;
    if (organization.staff?.length > 0) {
      ownerEmail = organization.staff?.find((staff) =>
        staff.roles.map((role) => role.type).includes(Role.Owner)
      )?.emailAddress;
    }
    return (
      <PanelRow key={`organization-${organization.id}`} isWithCells={true}>
        <PanelCell mods="u-size3of12">
          {organization.name}
          {organization.disabled && <span className="u-colorWarning5"> (Disabled)</span>}
          {organization.isTest && <span className="u-colorWarning5"> (Test)</span>}
        </PanelCell>
        <PanelCell mods="u-size1of12">{organization.id}</PanelCell>
        <PanelCell mods="u-size1of12">
          {DateService.format(new Date(organization.insertedAt), DateFormat.DD_MM_YYYY)}
        </PanelCell>
        <PanelCell mods="u-size2of12">{organization.churnZeroId}</PanelCell>
        <PanelCell mods="u-size3of12">{ownerEmail}</PanelCell>
        <PanelCell mods="u-size2of12 border-left u-textCenter">
          <div className="button-list">
            <Button size="small" icon="plus" iconPosition="left" onClick={() => showRolesModal(organization)}>
              Add Myself
            </Button>
            <Button size="small" icon="edit" iconPosition="left" onClick={() => showOrganization(organization)}>
              Edit Org
            </Button>
          </div>
        </PanelCell>
      </PanelRow>
    );
  };

  const renderOrganizationList = () => {
    if (loading) return renderLoading();

    if (data?.allOrganizations) {
      if (data.allOrganizations.length > 0) {
        const orgs = showTestOrgs ? data.allOrganizations : data.allOrganizations.filter((org) => !org.isTest);
        return <>{map(sortBy(orgs, ['name']), (org) => renderOrganization(org))}</>;
      }

      return renderNoOrgs();
    }

    return <div />;
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      getAllOrganizations({ variables: { searchTerm } });
    }
  };

  return (
    <div>
      <div className="page-content u-xs-padMd u-sm-spaceTopMd u-flexExpandSides">
        <LabelButton variantType="secondary" onClick={() => window.open(`${ADMIN_URL}/organizations`, '_blank')}>
          New Admin Console
        </LabelButton>
        <span className="sui-block sui-italic sui-mt-1">
          <span className="sui-text-red-40">*</span>Requires VPN
        </span>
        <div className="page-content-header Grid u-flexAlignItemsCenter sui-mt-4">
          <div className="Grid-cell u-xs-size1of2">
            <h1>Organizations</h1>
          </div>
          <div className="Grid-cell u-xs-size1of2 u-textRight">
            <Button color="primary" icon="plus" iconPosition="left" onClick={() => showOrganization(null)}>
              Create Organization
            </Button>
          </div>
        </div>

        <div className="Grid">
          <div className="Grid-cell u-spaceBottomMd u-flex u-spaceRightSm u-md-size5of12 u-flexJustifyStart">
            <Input
              type="text"
              name="searchTerm"
              className="u-size1of1"
              placeholder="Search by Org ID, Product UUID, Org Name, Staff Last Name, and Staff Email."
              inputProps={{
                onKeyDown,
                value: searchTerm,
                onChange: (e) => setsearchTerm(e.target.value),
              }}
            />
          </div>
          <div className="Grid-cell u-md-size6of12">
            <div className="Checkbox u-spaceTopSm">
              <input
                type="checkbox"
                className="Checkbox-input"
                name="includeTestOrgs"
                onClick={() => setShowTestOrgs(!showTestOrgs)}
                onChange={() => {}}
                checked={showTestOrgs}
              />
              <label htmlFor="addLegacySeason" className="Checkbox-label">
                Include Internal Test Orgs ({totalTestOrgs})
              </label>
            </div>
          </div>
        </div>

        <div className="page-content-body">
          <Panel isStriped={true} mods="Panel--dark-blue">
            <PanelBody>
              <PanelRow isWithCells={true} mods="Panel-row--header">
                <PanelCell mods="u-size3of12 Panel-row--title">Name</PanelCell>
                <PanelCell mods="u-size1of12 Panel-row--title">Gemini ID</PanelCell>
                <PanelCell mods="u-size1of12 Panel-row--title">Creation Date</PanelCell>
                <PanelCell mods="u-size2of12 Panel-row--title">C/L Product UUID</PanelCell>
                <PanelCell mods="u-size3of12 Panel-row--title">Email</PanelCell>
                <PanelCell mods="u-size2of12 Panel-row--title border-left u-textCenter">Manage</PanelCell>
              </PanelRow>
              {renderOrganizationList()}
            </PanelBody>
            <PanelFooter>
              <span />
            </PanelFooter>
          </Panel>
        </div>
      </div>
      {showOrgEditModal && (
        <OrganizationEdit
          employeeView={true}
          setShouldShowModal={setShowOrgEditModal}
          shouldShowModal={showOrgEditModal}
          selectedOrganizationId={+selectedOrganization?.id}
        />
      )}
      {showOrgConnectModal && (
        <OrganizationConnect
          setShouldShowModal={setShowOrgConnectModal}
          shouldShowModal={showOrgConnectModal}
          organizationId={null}
        />
      )}
      {showAddRolesModal && (
        <OrganizationAddRoles
          setShouldShowModal={setShowAddRolesModal}
          shouldShowModal={showAddRolesModal}
          selectedOrganization={selectedOrganization}
        />
      )}
    </div>
  );
};

export default OrganizationList;
